import React, { useState, useCallback, KeyboardEvent, MouseEvent } from 'react';
import styles from './CopyableText.module.css';
import { logger } from '../../utils/logger';

interface CopyableTextProps {
  text: string;
  copyValue?: string;
  label?: string;
  monospace?: boolean;
  className?: string;
  // TRAC-631: When true, only the clipboard icon button is rendered (no
  // visible text or label). `text` becomes the accessible name
  // (aria-label/title). Used for the internal copy-trace-link button.
  iconOnly?: boolean;
}

/**
 * Reusable component that displays text with a copy-to-clipboard button on hover.
 * Click anywhere on the text or the button to copy.
 * Shows a checkmark briefly after successful copy.
 */
const CopyableText: React.FC<CopyableTextProps> = ({
  text,
  copyValue,
  label,
  monospace = false,
  className = '',
  iconOnly = false
}) => {
  const [copied, setCopied] = useState(false);

  // In iconOnly mode `text` is a label (aria-label/title), NOT the payload — so the only
  // thing to copy is `copyValue`. Falling back to `text` there would copy the label
  // (e.g. "Copy trace link") instead of the actual value. In text mode, `text` IS the
  // visible content, so copying it is correct.
  const valueToCopy = iconOnly ? (copyValue ?? '') : (copyValue || text);
  const nothingToCopy = valueToCopy.length === 0;

  const handleCopy = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers

    if (nothingToCopy) return; // no-op — never copy the label or an empty string

    try {
      await navigator.clipboard.writeText(valueToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = valueToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (fallbackErr) {
        logger.error('Failed to copy text:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  }, [valueToCopy, nothingToCopy]);

  const containerClasses = [
    styles.container,
    iconOnly ? styles.iconOnly : '',
    monospace ? styles.monospace : '',
    copied ? styles.copied : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <span
      className={containerClasses}
      title={
        nothingToCopy
          ? (iconOnly ? `${text} — not available yet` : 'Nothing to copy')
          : (iconOnly ? text : `Click to copy: ${valueToCopy}`)
      }
      aria-label={iconOnly ? text : undefined}
      aria-disabled={nothingToCopy || undefined}
      style={nothingToCopy ? { opacity: 0.4, cursor: 'default' } : undefined}
      onClick={handleCopy}
      role="button"
      tabIndex={nothingToCopy ? -1 : 0}
      onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCopy(e);
        }
      }}
    >
      {!iconOnly && label && <span className={styles.label}>{label}</span>}
      {!iconOnly && <span className={styles.text}>{text}</span>}
      <span className={styles.copyIcon} aria-hidden="true">
        {copied ? (
          <svg
            width={iconOnly ? 16 : 14}
            height={iconOnly ? 16 : 14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        ) : (
          <svg
            width={iconOnly ? 16 : 14}
            height={iconOnly ? 16 : 14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        )}
      </span>
    </span>
  );
};

export default CopyableText;
